import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
  Collapse,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import { formatDate } from "../../utils/formatDate";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../ConfirmDialog";
import { toast } from "react-toastify";

export default function AreaStatus() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [areas, setAreas] = useState([]);
  const [query, setQuery] = useState("");
  const [filteredAreas, setFilteredAreas] = useState([]);
  const [loadingAreaSearchData, setLoadingAreaSearchData] = useState(true);
  const [orderBy, setOrderBy] = useState("time");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const isOlderThan48Hours = (sampleTime) => {
    if (!sampleTime) return true;

    try {
      const sampleDate = new Date(sampleTime);
      const currentDate = new Date();
      const diffInHours = (currentDate - sampleDate) / (1000 * 60 * 60);
      return diffInHours > 48;
    } catch (error) {
      console.error("Error parsing date:", error);
      return true;
    }
  };

  const getStatusIcon = (sampleTime) => {
    const isOld = isOlderThan48Hours(sampleTime);

    if (isOld) {
      return (
        <Tooltip title="Not working (Sample time > 48 hours)">
          <ErrorIcon sx={{ color: "error.main" }} />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Working (Sample time within 48 hours)">
          <CheckCircleIcon sx={{ color: "success.main" }} />
        </Tooltip>
      );
    }
  };

  const sortAreas = (areasToSort) => {
    if (!sortConfig.key) return areasToSort;

    return [...areasToSort].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      if (typeof aValue === "string") {
        if (sortConfig.direction === "asc") return aValue.localeCompare(bValue);
        else return bValue.localeCompare(aValue);
      } else if (typeof aValue === "number") {
        if (sortConfig.direction === "asc") return aValue - bValue;
        else return bValue - aValue;
      } else {
        return 0;
      }
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const fetchAreas = async () => {
    try {
      setLoadingAreaSearchData(true);
      let Api_return = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/area-status`
      );
      const data = await Api_return.data.data
        .filter((area) => area.time !== "N/A" && area.name !== "unknown")
        .map((area) => {
          return {
            id: area.id,
            name: area.name,
            time: area.time,
            regionroute: area.regionroute,
            CtrlType: area.CtrlType == "WellPro II" ? "WP" : area.CtrlType,
            SDcode: area.SDcode,
            SDcodeDesc: area.SDcodeDesc,
            state: area.state,
            stateDesc: area.stateDesc,
            status: area.status,
            regionName: area.regionName,
            routeName: area.routeName,
            groupName: area.groupName,
          };
        });
      console.log(data, "iiiiiiiiiiii");
      setAreas(data);
      setFilteredAreas(data);
    } catch (err) {
      console.error("Error fetching devices:", err);
    } finally {
      setLoadingAreaSearchData(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const handleSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setQuery(q);
    const filtered = areas.filter((u) => {
      const name = u.name || "";
      const id = u.id || "";
      const type = u.CtrlType || "";
      const shutdownEnum = u.SDcodeDesc || "";
      return (
        name.toLowerCase().includes(q) ||
        id.toLowerCase().includes(q) ||
        type.toLowerCase().includes(q) ||
        shutdownEnum.toLowerCase().includes(q)
      );
    });
    setFilteredAreas(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const gotoUnitPage = (id) => {
    navigate(`../device/${id}/engine-data`);
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

 const exportToCSV = () => {
  const csvContent = [
    [
      "Unit Name",
      "Modem Serial Number",
      "Region/Route",
      "Controller",
      "Shutdown Enum",
      "State",
      "Sample Time",
      "Status",
    ],
    ...sortedUnits.map((area) => {
      // 🔁 Same logic as UI
      const regionRoute =
        area.groupName && area.routeName
          ? `${area.groupName}-${area.routeName}`
          : area.groupName && area.regionName
          ? `${area.groupName}-${area.regionName}`
          : area.groupName
          ? area.groupName
          : "N/A";

      const status = isOlderThan48Hours(area.time)
        ? "Not Working"
        : "Working";

      return [
        area.name,
        area.id,
        regionRoute,
        area.CtrlType,
        `${area.SDcodeDesc} (${area.SDcode})`,
        area.stateDesc,
        area.time,
        status,
      ];
    }),
  ];

  const csvString = csvContent
    .map((row) =>
      row
        .map((field) =>
          `"${String(field ?? "").replace(/"/g, '""')}"`
        )
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csvString], {
    type: "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = "Area_Status_data.csv";
  link.click();

  handleExportMenuClose();
  toast.success("Exported Area Status view to CSV");
};


  const exportToExcel = () => {
    const headers = [
      "Unit Name",
      "Modem Serial Number",
      "Region/Route",
      "Controller",
      "Shutdown Enum",
      "State",
      "Sample Time",
      "Status",
    ];

    const rows = sortedUnits.map((area) => {
      // 🔁 Same logic as UI
      const regionRoute =
        area.groupName && area.routeName
          ? `${area.groupName}-${area.routeName}`
          : area.groupName && area.regionName
          ? `${area.groupName}-${area.regionName}`
          : area.groupName
          ? area.groupName
          : "N/A";

      const status = isOlderThan48Hours(area.time) ? "Not Working" : "Working";

      return [
        area.name,
        area.id,
        regionRoute,
        area.CtrlType,
        `${area.SDcodeDesc} (${area.SDcode})`,
        area.stateDesc,
        area.time,
        status,
      ];
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Units");
    XLSX.writeFile(workbook, "Area_Status_data.xlsx");

    handleExportMenuClose();
    toast.success("Exported Area Status view to Excel");
  };
  [];

  const exportToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(10);
    doc.text("Units Report", 40, 30);

    const headers = [
      [
        "Unit Name",
        "Modem Serial Number",
        "Region/Route",
        "Controller",
        "Shutdown Enum",
        "State",
        "Sample Time",
        "Status",
      ],
    ];

    const rows = sortedUnits.map((area) => {
      // 🔁 Same logic as UI
      const regionRoute =
        area.groupName && area.routeName
          ? `${area.groupName}-${area.routeName}`
          : area.groupName && area.regionName
          ? `${area.groupName}-${area.regionName}`
          : area.groupName
          ? area.groupName
          : "N/A";

      const status = isOlderThan48Hours(area.time) ? "Not Working" : "Working";

      return [
        area.name,
        area.id,
        regionRoute,
        area.CtrlType,
        `${area.SDcodeDesc} (${area.SDcode})`,
        area.stateDesc,
        area.time,
        status,
      ];
    });

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [41, 128, 185] },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 75 },
        2: { cellWidth: 60 },
        3: { cellWidth: 60 },
        4: { cellWidth: 160 },
        5: { cellWidth: 40 },
        6: { cellWidth: 120 },
        7: { cellWidth: 70 },
      },
    });

    doc.save("Area_Status_data.pdf");
    handleExportMenuClose();
    toast.success("Exported Area_Status_data view to PDF");
  };

  const copyToClipboard = () => {
    const headers =
      "Unit Name\tModem Serial Number\tRegion/Route\tController\tshutdown EnumstateDesc\tSample Time\tStatus";

    const textContent = sortedUnits
      .map((area) => {
        // 🔁 Same logic as UI
        const regionRoute =
          area.groupName && area.routeName
            ? `${area.groupName}-${area.routeName}`
            : area.groupName && area.regionName
            ? `${area.groupName}-${area.regionName}`
            : area.groupName
            ? area.groupName
            : "N/A";

        const status = isOlderThan48Hours(area.time)
          ? "Not Working"
          : "Working";

        return [
          area.name,
          area.id,
          regionRoute,
          area.CtrlType,
          `${area.SDcodeDesc}(${area.SDcode})`,
          area.stateDesc,
          area.time,
          status,
        ].join("\t");
      })
      .join("\n");

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => {
        console.log("Area Status view data copied to clipboard");
        toast.success("copied to clipboard Area Status data");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        toast.error("Failed to copy data");
      });

    handleExportMenuClose();
  };

  const sortedUnits = sortAreas(filteredAreas);
  const paginatedUnits = sortedUnits.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        overflow: "auto",
        height: { xs: "50vh", md: "calc(100vh - 350px)" },
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "none",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {/* Mobile expand button column */}
            <TableCell
              sx={{
                display: { xs: "table-cell", md: "none" },
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "12px",
                padding: "12px 8px",
                width: "50px",
              }}
            >
              Details
            </TableCell>

            <TableCell
              sx={{
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
              sortDirection={
                sortConfig.key === "name" ? sortConfig.direction : false
              }
            >
              <TableSortLabel
                active={sortConfig.key === "name"}
                direction={
                  sortConfig.key === "name" ? sortConfig.direction : "asc"
                }
                onClick={() => handleSort("name")}
              >
                Unit Name
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              Modem Serial Number
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              Region/Route
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              Controller
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              Shutdown Enum
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
            >
              State
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                cursor: "pointer",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                padding: "8px",
              }}
              sortDirection={
                sortConfig.key === "time" ? sortConfig.direction : false
              }
            >
              <TableSortLabel
                active={sortConfig.key === "time"}
                direction={
                  sortConfig.key === "time" ? sortConfig.direction : "asc"
                }
                onClick={() => handleSort("time")}
              >
                Sample Time
              </TableSortLabel>
            </TableCell>

            <TableCell
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px",
              }}
            >
              Unit Page
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedUnits.length > 0 ? (
            paginatedUnits.map((area, index) => (
              <React.Fragment key={area.id}>
                <TableRow
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  {/* Expand Button - Mobile Only */}
                  <TableCell
                    sx={{
                      display: { xs: "table-cell", md: "none" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(area.id)}
                      sx={{ color: "white" }}
                    >
                      {expandedRows[area.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {area.name}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {area.id}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {area.groupName && area.routeName
                      ? `${area.groupName}-${area.routeName}`
                      : area.groupName && area.regionName
                      ? `${area.groupName}-${area.regionName}`
                      : area.groupName
                      ? area.groupName
                      : "N/A"}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {area.CtrlType}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {area.SDcodeDesc + "(" + area.SDcode + ")"}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {area.stateDesc + "(" + area.state + ")"}
                  </TableCell>

                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {formatDate(area.time)}
                      {getStatusIcon(area.time)}
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => gotoUnitPage(area.id)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>

                {/* Mobile Collapsed Row */}
                <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={4}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    <Collapse
                      in={expandedRows[area.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ py: 2, px: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Region/Route:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {area.groupName && area.routeName
                                ? `${area.groupName}-${area.routeName}`
                                : area.groupName && area.regionName
                                ? `${area.groupName}-${area.regionName}`
                                : area.groupName
                                ? area.groupName
                                : "N/A"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Controller:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {area.CtrlType}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Shutdown Enum:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {area.SDcodeDesc + "(" + area.SDcode + ")"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              State:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {area.stateDesc + "(" + area.state + ")"}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Sample Time:
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "white" }}
                              >
                                {formatDate(area.time)}
                              </Typography>
                              {getStatusIcon(area.time)}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                No areas found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 0 },
        height: "100%",
        position: "relative",
      }}
    >
      {loadingAreaSearchData && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontSize={{ xs: "1.15rem", sm: "2rem", md: "2rem" }}
              fontWeight="bold"
            >
              Area Status
            </Typography>
            <Chip
              label={`${filteredAreas.length} units`}
              color="primary"
              size="small"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              sx={{ borderRadius: 0 }}
              variant="outlined"
              color="white"
              startIcon={<GetAppIcon />}
              onClick={handleExportMenuOpen}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TextField
          label="Search by Name, controller, shutdown Enum or ID"
          variant="outlined"
          value={query}
          onChange={handleSearch}
          fullWidth
          sx={{ mb: 2 }}
        />

        {renderTable()}

        <TablePagination
          component="div"
          count={filteredAreas.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{ mt: 2 }}
        />

        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={copyToClipboard}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToCSV}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToPDF}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>
    </Box>
  );
}
